/**
 * Brand art and listing autofill — the two provisioning steps that make a new school's app look and
 * read like the school's with nobody at the editor (SCH-2236).
 *
 * POST /v1/mobile-apps/:id/brand-art/generate        (super-admin)
 * POST /v1/mobile-apps/:id/store-listing/autofill    (super-admin)
 *
 * Both are idempotent: they fill what is missing and leave what is set, so a second press costs
 * nothing and writes nothing. `force` on brand art redoes every slot and colour.
 */

import { z } from 'zod'

const HexColor = z.string().regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, 'Must be a hex colour (e.g. #04350D)')

export const GenerateBrandArtRequestSchema = z.object({
  /** Regenerate every brand slot and rewrite every colour, even ones already set. */
  force: z.boolean().default(false),
  /** Plan only: report what would be generated and which colours set — no model call, no write. */
  dryRun: z.boolean().default(false),
  /** The brand colours to use. Absent ⇒ the app's branding, then the school's website, then a default. */
  colors: z.object({ primary: HexColor.optional(), accent: HexColor.optional() }).optional(),
  /** An existing logo to derive the slots from instead of generating an emblem. https only. */
  logoUrl: z.string().url().startsWith('https://').optional(),
  /** The school's website, read for its colours. Absent ⇒ the store listing's vetted marketing URL. */
  websiteUrl: z.string().url().startsWith('https://').optional(),
  /** The most this run may spend on the image model, in USD. */
  costCapUsd: z.number().positive().max(10).optional()
})
export type GenerateBrandArtRequest = z.infer<typeof GenerateBrandArtRequestSchema>

export const BrandArtSlotResultSchema = z.object({
  slotName: z.string(),
  width: z.number(),
  height: z.number(),
  bytes: z.number()
})

export const GenerateBrandArtResponseSchema = z.object({
  data: z.object({
    appId: z.string(),
    /** Nothing was missing: no model call and no write. */
    skipped: z.boolean(),
    /** A plan only (`dryRun`): nothing was generated or written. */
    dryRun: z.boolean(),
    /** What this run set out to do (or, on a dry run, would do). */
    planned: z.object({
      slots: z.array(z.string()),
      colors: z.array(z.string()),
      emblemSource: z.enum(['generated', 'existing-logo']).nullable()
    }),
    committed: z.array(BrandArtSlotResultSchema),
    /** Slots that already held an asset and were left alone. */
    kept: z.array(z.string()),
    failed: z.array(z.object({ slotName: z.string(), reason: z.string() })),
    /** The branding and registry keys this run wrote (`brandingConfig.button`, `splashBackgroundColor`, …). */
    colorsWritten: z.array(z.string()),
    palette: z.object({
      primary: z.string(),
      accent: z.string(),
      splashBackgroundColor: z.string(),
      source: z.enum(['request', 'branding', 'splash', 'website', 'default']),
      websiteUrl: z.string().nullable(),
      contrast: z.array(z.object({ pair: z.string(), ratio: z.number(), min: z.number() }))
    }),
    emblemSource: z.enum(['generated', 'existing-logo']).nullable(),
    model: z.string().nullable(),
    /** What the image model charged for this run, in USD. */
    costUsd: z.number(),
    attempts: z.array(
      z.object({
        kind: z.enum(['emblem', 'scene']),
        slot: z.string().optional(),
        ok: z.boolean(),
        reason: z.string().nullable(),
        costUsd: z.number()
      })
    ),
    /** The geometry gate's findings on the last refused emblem set; empty when the accepted set passed. */
    geometry: z.array(
      z.object({ slot: z.string(), check: z.string(), severity: z.enum(['fail', 'warn']), message: z.string() })
    )
  }),
  message: z.string()
})
export type GenerateBrandArtResponse = z.infer<typeof GenerateBrandArtResponseSchema>

export const AutofillStoreListingResponseSchema = z.object({
  data: z.object({
    appId: z.string(),
    /** A plan only (`dryRun`): `filled` names what a real run would fill; nothing was written. */
    dryRun: z.boolean(),
    /** The never-set fields this call filled, per store. */
    filled: z.object({ apple: z.array(z.string()), play: z.array(z.string()) }),
    /** The AI's school-specific copy was used (the school was confirmed on its own website). */
    aiUsed: z.boolean(),
    websiteUrl: z.string().nullable(),
    identityConfidence: z.number().nullable(),
    warnings: z.array(z.string())
  }),
  message: z.string()
})
export const AutofillStoreListingRequestSchema = z.object({
  /** Plan only: report the fields that would be filled — no AI call, no write. */
  dryRun: z.boolean().default(false)
})
export type AutofillStoreListingRequest = z.infer<typeof AutofillStoreListingRequestSchema>

export type AutofillStoreListingResponse = z.infer<typeof AutofillStoreListingResponseSchema>
